import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import { cn } from '@/lib/utils';
import { FileText, Calendar, User, LayoutDashboard, ChevronRight, Mail } from 'lucide-react';

interface ClientPortalLayoutProps {
  children: ReactNode;
}

export default function ClientPortalLayout({ children }: ClientPortalLayoutProps) {
  const location = useLocation();
  
  const navItems = [
    { href: '/client-portal', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/client-portal/documents', label: 'My Documents', icon: FileText },
    { href: '/client-portal/appointments', label: 'My Appointments', icon: Calendar },
    { href: '/client-portal/profile', label: 'My Profile', icon: User },
    { href: '/client-portal/email-history', label: 'Email History', icon: Mail },
  ];

  const getBreadcrumbs = () => {
    const path = location.pathname;
    const breadcrumbs = [{ label: 'Home', href: '/' }];
    
    if (path.includes('/client-portal')) {
      breadcrumbs.push({ label: 'Client Portal', href: '/client-portal' });
      
      if (path.includes('/documents')) {
        breadcrumbs.push({ label: 'Documents', href: '/client-portal/documents' });
      } else if (path.includes('/appointments')) {
        breadcrumbs.push({ label: 'Appointments', href: '/client-portal/appointments' });
      } else if (path.includes('/profile')) {
        breadcrumbs.push({ label: 'Profile', href: '/client-portal/profile' });
      } else if (path.includes('/email-history')) {
        breadcrumbs.push({ label: 'Email History', href: '/client-portal/email-history' });
      }
    }
    
    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        {/* Breadcrumbs */}
        <div className="border-b bg-muted/30">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center space-x-2 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <div key={crumb.href} className="flex items-center">
                  {index > 0 && <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground" />}
                  <Link
                    to={crumb.href}
                    className={cn(
                      "hover:text-primary transition-colors",
                      index === breadcrumbs.length - 1
                        ? "text-foreground font-medium"
                        : "text-muted-foreground"
                    )}
                  >
                    {crumb.label}
                  </Link>
                </div>
              ))}
            </nav>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Navigation */}
            <aside className="lg:w-64 shrink-0">
              <nav className="space-y-1 sticky top-8">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground font-medium"
                          : "hover:bg-muted text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              {children}
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
}
